import type { ReactNode } from "react";

import { type Row, type Table as TableType, flexRender } from "@tanstack/react-table";
import { RotateCcwIcon, TriangleAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableRow } from "@/components/ui/table";

import { EmptyState } from "@/components/shared/empty";
import { SimpleTooltip } from "@/components/shared/simple-tooltip";

import { cn } from "@/lib/utils";

import { STICKY_CELL, STICKY_END_EDGE, STICKY_START_EDGE } from "./sticky";
import { TableHeaderComponent } from "./table-header";

/* Jadval moduli — sahifa yig'adigan qismlar. `ReactTable` faqat jadvalning
   o'zini chizadi: filtrlar, sahifalash va ustun ko'rinishi uning ustida alohida
   turadi, shuning uchun sahifa ularni o'zi joylashtiradi. Komponent
   brb-workspace'ning `@brb/shared` paketidan ko'chirilgan. */
export { Filters } from "./filters";
export { TablePagination } from "./pagination";
export { ColumnVisibilityControl } from "./column-visibility-control";

/**
 * Katakning o'lchami — butun panel uchun bitta joyda.
 *
 * Balandlik `min-height` bo'lib ishlaydi: sig'magan katak baribir o'zi
 * kerakligicha cho'ziladi, ya'ni bu raqam qatorning ENG PASTI.
 */
const CELL = "h-6 border px-2 py-0.5";

/* Qator balandligini matn emas, amallar ustuni belgilardi: tugma `icon-sm`,
   ya'ni 32px, va katakning to'ldirishi ustiga qo'shilib qatorni 40px qilardi.
   Shu yerda u qator ritmiga tushiriladi. Faqat balandlik: kengligi tegilmaydi,
   ya'ni matnli tugma siqilmaydi. `a` ham kerak — `asChild` bilan tugma havola
   bo'lib chiqadi. */
const ACTION_CELL = "[&_a]:h-7 [&_button]:h-7";

const NARROW_COLUMNS = ["actions", "id", "index", "select"];

/** Kengligi berilmagan ustunning ulushi — jadval eng kichik kengligi uchun. */
const DEFAULT_COLUMN_WIDTH = 160;

interface ReactTableProps<TData> {
  table: TableType<TData>;
  isLoading: boolean;
  /**
   * So'rov yiqildi. Bo'sh ro'yxat bilan yiqilgan so'rov bir xil ko'rinmasin:
   * xatoda sabab va "Qayta urinish" tugmasi chiqadi.
   */
  isError?: boolean;
  onRetry?: () => void;
  /** Bo'sh holat — sabab va keyingi qadam bilan (`EmptyState`). */
  empty?: ReactNode;
  wrapperClassName?: string;
  className?: string;
  headerClassName?: string;
  rowClassName?: string | ((row: Row<TData>) => string);
  cellClassName?: string;
  /** "Hozir shu yozuv bilan ishlanyapti" — masalan, o'ng panelda ochiq element. */
  rowId?: string;
  pagination?: ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  onRowDoubleClick?: (row: Row<TData>) => void;
  /**
   * Bosiladigan qatorning skrinrider nomi ("… ni ochish"). Berilsa qator
   * tugma sifatida e'lon qilinadi — sichqonchasiz odam ham yozuvni ochadi.
   */
  rowLabel?: (row: Row<TData>) => string;
  /**
   * `card` (standart) — jadval o'z kartasida: foni, chegarasi va soyasi bor.
   * Kartasiz jadval sahifa foniga qo'shilib ketardi. Tashqi kataklarning
   * chegarasi olinadi — karta chegarasi bilan ikki qavat chiziq bo'lmasin.
   * `plain` — jadval allaqachon boshqa kartaning ichida bo'lsa.
   */
  variant?: "plain" | "card";
}

/* Kartada tashqi chiziqlarni karta chegarasi chizadi: chetki kataklarning
   yon chegarasi va sarlavhaning yuqori chegarasi olinadi. */
const CARD_TABLE =
  "[&_tr>*:first-child]:border-s-0 [&_tr>*:last-child]:border-e-0 [&_th]:border-t-0";

export function ReactTable<TData>({
  table,
  isLoading = false,
  isError = false,
  onRetry,
  empty,
  pagination,
  wrapperClassName,
  className,
  headerClassName,
  rowClassName,
  cellClassName,
  rowId,
  onRowClick,
  onRowDoubleClick,
  rowLabel,
  variant = "card",
}: Readonly<ReactTableProps<TData>>) {
  const isCard = variant === "card";
  const { t } = useTranslation();
  const visibleColumns = table.getVisibleLeafColumns();

  /* `table-fixed` + `w-full` da brauzer kengligi berilmagan ustunlarni
     konteynerga SIQADI: tor oynada ular nolga tushib, ichidagi nishon qo'shni
     ustun ustiga chiqardi. Eng kichik kenglik — ustunlar yig'indisi: undan
     tor joyda jadval siqilmaydi, aylanadi. */
  const minWidth = visibleColumns.reduce((sum, column) => {
    const width = column.columnDef.meta?.width;
    return sum + (typeof width === "number" ? width : DEFAULT_COLUMN_WIDTH);
  }, 0);

  const renderSkeletonRows = (rows: number, cols: number) => {
    const rowKeys = Array.from({ length: rows }, (_, i) => `skeleton-row-${i}`);
    const colKeys = Array.from({ length: cols }, (_, i) => `skeleton-col-${i}`);
    return rowKeys.map(rowKey => (
      <TableRow key={rowKey}>
        {colKeys.map(colKey => (
          <TableCell key={`${rowKey}-${colKey}`} className={cn(CELL, "text-center")}>
            <Skeleton className="h-full min-h-4 w-full rounded-sm" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  /* Bo'sh holat jadval katagi ichida emas, jadvaldan keyin chiziladi (pastda):
     katak balandligini `100vh - N` deb taxmin qilish masshtab o'zgarganda
     yolg'on chiqadi, flex esa qolgan joyni o'zi o'lchaydi. */
  const isEmpty = !isLoading && (isError || !table.getRowModel().rows?.length);

  const renderBody = () => {
    if (isLoading) {
      /* Skeleton kutilayotgan sahifa hajmini TO'LIQ takrorlaydi — ma'lumot
         kelganda joylashuv sakramaydi. */
      const rows = table.getState().pagination.pageSize;
      return renderSkeletonRows(rows, visibleColumns.length);
    }

    if (isEmpty) return null;

    return table.getRowModel().rows.map(row => (
      <TableRow
        key={row.id}
        role={rowLabel ? "button" : "row"}
        aria-label={rowLabel?.(row)}
        data-state={row.getIsSelected() && "selected"}
        data-current={rowId === row.id || undefined}
        className={cn(
          "group/row",
          /* Rang token'dan (`--highlight`): qorong'i mavzu ham o'sha token
             ichida hal bo'ladi va palitra bir joyda qoladi. */
          rowId === row.id &&
            "bg-highlight even:bg-highlight hover:bg-highlight-hover even:hover:bg-highlight-hover",
          /* `rowClassName` dan oldin: sahifa ayrim qatorni ochib bo'lmasligini
             bilishi mumkin va o'sha qatorga `cursor-default` bera olishi kerak.
             Keyin tursa tailwind-merge doim `cursor-pointer` ni tanlardi. */
          onRowClick && "focus-visible:bg-muted cursor-pointer outline-none",
          typeof rowClassName === "function" ? rowClassName(row) : rowClassName,
        )}
        tabIndex={onRowClick ? 0 : undefined}
        onClick={() => onRowClick?.(row)}
        onDoubleClick={() => onRowDoubleClick?.(row)}
        onKeyDown={event => {
          // Qator ichidagi tugma yoki havolaning Enter'i qatorni ochmasin.
          if (event.target !== event.currentTarget) return;
          if (onRowClick && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onRowClick(row);
          }
        }}
      >
        {row.getVisibleCells().map(cell => {
          const columnMeta = cell.column.columnDef.meta;
          const value = cell.getValue();
          const colId = cell.column.id;

          return (
            <TableCell
              key={cell.id}
              role="cell"
              className={cn(
                CELL,
                "text-xs leading-tight",
                {
                  "w-11 min-w-11 text-center": NARROW_COLUMNS.includes(colId),
                  [`${STICKY_CELL} ${STICKY_END_EDGE} right-0 ${ACTION_CELL}`]: colId === "actions",
                  [`${STICKY_CELL} ${STICKY_START_EDGE} left-0`]: colId === "select",
                },
                cellClassName,
              )}
              style={{ width: columnMeta?.width }}
            >
              {columnMeta?.hasTooltip &&
              (typeof value === "string" || typeof value === "number") ? (
                <div className="flex justify-start">
                  <SimpleTooltip description={String(value)}>
                    <div className="truncate" style={{ maxWidth: columnMeta?.width || "auto" }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </SimpleTooltip>
                </div>
              ) : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  const renderEmpty = () => {
    if (isError) {
      return (
        <EmptyState
          icon={TriangleAlertIcon}
          title={t("COMMON.LOAD_FAILED")}
          description={t("COMMON.LOAD_FAILED_HINT")}
          className="border-none bg-transparent py-8"
          action={
            onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                <RotateCcwIcon className="size-4" />
                {t("COMMON.RETRY")}
              </Button>
            )
          }
        />
      );
    }

    return (
      empty ?? <EmptyState title={t("TABLE.NO_DATA")} className="border-none bg-transparent py-8" />
    );
  };

  return (
    // Sahifa bergan balandlikni to'liq egallaydi, viewport'dan chegirmani
    // taxmin qilmaydi: sahifa ildizi qobiqning aylanuvchi qutisi ichida
    // `h-full`, shuning uchun `flex-1` jadvalni istalgan masshtabda aynan
    // pastki chetga olib boradi. `min-h-0` — tana qisqarib aylanadi va
    // sahifalashni ekrandan itarib chiqarmaydi.
    <div
      data-variant={variant}
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        { "justify-between": !!pagination },
        isCard && "bg-card overflow-hidden rounded-xl border shadow-xs",
        wrapperClassName,
      )}
    >
      {/* Aylanish jadvalning O'ZIDA: sahifa bergan balandlikdan oshgan
          qatorlar shu yerda aylanadi, sarlavha (`sticky`) tepada qoladi va
          sahifalash pastki chekkadan siljimaydi. `ui/table` dagi `Table` o'z
          `overflow-x-auto` qutisini qo'shadi va yopishgan sarlavha o'shanga
          yopishib qolardi — `components/ui` qo'lda tahrirlanmaydi, shuning
          uchun quti shu yerda chiziladi. `flex-1` faqat qator bo'lganda: bo'sh
          jadvalda qolgan joyni bo'sh holat bandi oladi. */}
      <div
        data-slot="table-container"
        className={cn("thin-scrollbar relative min-h-0 w-full overflow-auto", !isEmpty && "flex-1")}
      >
        <table
          data-slot="table"
          // Ustunlar kengligi ma'lumotga bog'liq EMAS: jadval doim `table-fixed`.
          // brb'dagi asl kod bo'sh jadvalda `table-auto` ga o'tardi — filtr
          // natijasi bo'sh chiqqanda kengliklar faqat sarlavha matnidan qayta
          // hisoblanib, ustunlar yon tomonga "sakrardi".
          className={cn(
            "w-full table-fixed caption-bottom text-sm",
            isCard && CARD_TABLE,
            className,
          )}
          style={{ minWidth }}
        >
          <TableHeaderComponent headerClassName={headerClassName} table={table} />
          <TableBody aria-busy={isLoading}>{renderBody()}</TableBody>
        </table>
      </div>

      {isEmpty && <div className="flex flex-1 items-center justify-center">{renderEmpty()}</div>}

      {pagination && (
        <div className={cn(isCard && "px-3 pb-2")}>
          <Separator />
          {pagination}
        </div>
      )}
    </div>
  );
}
